import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, DatePicker, Input, Modal, Space, Spin, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  fetchInvoiceCorrectionState,
  invoiceWorkflowApi,
  withdrawInvoice,
  type InvoiceCorrectionSummary,
} from '../api/invoiceWorkflowApi';
import type { InvoiceCorrectionState } from '../../../dto/invoiceCorrection';
import type { WorkflowInvoice, WorkflowInvoiceDirection } from '../../../types/workflowInvoice';
import { getInvoiceImportErrorMessage } from '../invoiceImportErrorMessages';
import { formatRemainderLines } from '../invoiceCoverageLabels';
import { useFeedback } from '../../../utils/feedback';
import { formatMoney } from '../../../utils/money';
import { formatUnit } from '../../../utils/units';

const REASON_MIN = 10;
const REASON_MAX = 500;

interface InvoiceCorrectionsPanelProps {
  direction: WorkflowInvoiceDirection;
  invoice: WorkflowInvoice;
  /** Approval preview of a correction draft: net before/after in PLN. */
  preview?: InvoiceCorrectionSummary | null;
  /** Open another invoice (a correction draft, the original) on this screen. */
  onOpen: (invoiceId: string) => void;
  /** The invoice or its order changed (withdrawal): reload the card. */
  onChanged: () => void;
}

const pln = (value: number | null | undefined) => (value == null ? '—' : `${formatMoney(value, 'pl')} PLN`);

/**
 * WP2 (docs/decisions/2026-09-24-wp2-korekty-faktur.md): correction invoices
 * and withdrawal on the invoice card. A correction is entered from outside
 * like the original (manual or OCR) and states the invoice after the
 * correction; only the difference reaches the result, in the correction's
 * month. Withdrawal (administrator, with a reason) reverses what the invoice
 * recognized; the invoice itself stays in history.
 */
export default function InvoiceCorrectionsPanel({ direction, invoice, preview, onOpen, onChanged }: InvoiceCorrectionsPanelProps) {
  const { t } = useTranslation(['invoices', 'common']);
  const feedback = useFeedback();
  const [state, setState] = useState<InvoiceCorrectionState | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [month, setMonth] = useState(dayjs().format('YYYY-MM'));
  const invoiceId = invoice.id;

  const load = useCallback(() => {
    if (!invoiceId) return undefined;
    let active = true;
    setFailed(false);
    fetchInvoiceCorrectionState(direction, invoiceId)
      .then((value) => { if (active) setState(value); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [direction, invoiceId]);
  useEffect(() => load(), [load, invoice.version, invoice.status]);

  if (!invoiceId) return null;
  if (failed) {
    return <Alert type="warning" showIcon message={t('invoices:workflow.corrections.loadFailed')}
      action={<Button onClick={() => load()}>{t('common:refresh')}</Button>} />;
  }
  if (!state) return <Spin size="small" />;

  const createCorrection = async () => {
    setBusy(true);
    try {
      const draft = await invoiceWorkflowApi(direction).createCorrection(invoiceId);
      feedback.success(t('invoices:workflow.corrections.created'));
      if (draft.id) onOpen(draft.id);
    } catch (cause) {
      feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:workflow.corrections.createFailed'));
    } finally {
      setBusy(false);
    }
  };
  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const withdraw = async () => {
    if (!reasonValid || !month) return;
    setBusy(true);
    try {
      await withdrawInvoice(direction, invoiceId, { reason: trimmedReason, recognitionMonth: month });
      feedback.success(t('invoices:workflow.corrections.withdrawn', { number: state.original?.number || '' }));
      setWithdrawOpen(false);
      setReason('');
      onChanged();
      load();
    } catch (cause) {
      feedback.error(getInvoiceImportErrorMessage(cause, t, 'invoices:workflow.corrections.withdrawFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (state.isCorrection) {
    return (
      <Alert
        type="info"
        showIcon
        data-testid="invoice-correction-banner"
        message={t('invoices:workflow.corrections.draftTitle', { number: state.original?.number || '' })}
        description={<Space direction="vertical" size={4}>
          <span>{t('invoices:workflow.corrections.draftHint')}</span>
          {preview && <span data-testid="invoice-correction-delta">{t('invoices:workflow.corrections.delta', {
            before: pln(preview.netBeforePLN), after: pln(preview.netAfterPLN), delta: pln(preview.netDeltaPLN),
          })}</span>}
          {state.original && <Button type="link" style={{ padding: 0 }} onClick={() => onOpen(state.original!.id)}>
            {t('invoices:workflow.corrections.openOriginal')}
          </Button>}
        </Space>}
      />
    );
  }

  return (
    <Card size="small" title={t('invoices:workflow.corrections.title')} data-testid="invoice-corrections-panel">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {state.withdrawal && <Alert type="warning" showIcon data-testid="invoice-withdrawn"
          message={t('invoices:workflow.corrections.withdrawnTitle', { month: state.withdrawal.recognitionMonth })}
          description={t('invoices:workflow.corrections.withdrawnHint', {
            reason: state.withdrawal.reason, name: state.withdrawal.withdrawnByName || '—',
          })} />}
        {!state.withdrawal && <Typography.Text type="secondary">{t('invoices:workflow.corrections.hint')}</Typography.Text>}
        {state.corrections.length > 0 && <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {state.corrections.map((row) => (
            <Space key={row.id} wrap>
              <Button type="link" style={{ padding: 0 }} onClick={() => onOpen(row.id)}>
                {row.number || t('invoices:workflow.corrections.noNumber')}
              </Button>
              <Tag color={row.status === 'APPROVED' ? 'green' : 'gold'}>
                {t(`invoices:workflow.corrections.status.${row.status === 'APPROVED' ? 'approved' : 'draft'}`)}
              </Tag>
              {row.recognitionMonth && <Typography.Text type="secondary">{row.recognitionMonth}</Typography.Text>}
              <Typography.Text>{pln(row.netPLN)}</Typography.Text>
            </Space>
          ))}
          {state.effectiveInvoiceId && state.effectiveInvoiceId !== state.original?.id && (
            <Typography.Text strong data-testid="invoice-effective-net">
              {t('invoices:workflow.corrections.effective', { amount: pln(state.effectiveNetPLN) })}
            </Typography.Text>
          )}
        </Space>}
        {state.uninvoicedLines.length > 0 && <Alert type="info" showIcon data-testid="invoice-uninvoiced-lines"
          message={t(`invoices:workflow.corrections.${direction === 'PURCHASE' ? 'uninvoicedPurchase' : 'uninvoicedSales'}`, {
            lines: formatRemainderLines(state.uninvoicedLines),
          })} />}
        {state.issuedOverInvoice.length > 0 && <Alert type="warning" showIcon data-testid="invoice-issued-over-invoice"
          message={t('invoices:workflow.corrections.issuedOverInvoice', {
            lines: state.issuedOverInvoice.map((line) => `${line.name} (${line.quantity.toLocaleString('pl-PL')} ${formatUnit(line.unit)})`).join(', '),
          })} />}
        {state.openResettlementEntries > 0 && <Alert type="info" showIcon
          message={t('invoices:workflow.corrections.openEntries', { count: state.openResettlementEntries })} />}
        {(state.canCorrect || state.canWithdraw) && <Space wrap>
          {state.canCorrect && <Button type="primary" loading={busy} onClick={() => void createCorrection()} data-testid="invoice-create-correction">
            {t('invoices:workflow.corrections.create')}
          </Button>}
          {state.canWithdraw && <Button danger disabled={busy} onClick={() => setWithdrawOpen(true)} data-testid="invoice-withdraw">
            {t('invoices:workflow.corrections.withdraw')}
          </Button>}
        </Space>}
      </Space>
      <Modal
        open={withdrawOpen}
        title={t('invoices:workflow.corrections.withdrawTitle', { number: state.original?.number || '' })}
        okText={t('invoices:workflow.corrections.withdrawConfirm')}
        okButtonProps={{ danger: true, disabled: !reasonValid || !month, loading: busy }}
        cancelText={t('common:cancel')}
        onOk={() => void withdraw()}
        onCancel={() => { if (!busy) setWithdrawOpen(false); }}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Typography.Paragraph>{t(`invoices:workflow.corrections.${direction === 'PURCHASE' ? 'withdrawEffectPurchase' : 'withdrawEffectSales'}`)}</Typography.Paragraph>
          <label>
            <span>{t('invoices:workflow.corrections.withdrawReason')}</span>
            <Input.TextArea aria-label={t('invoices:workflow.corrections.withdrawReason')} value={reason} maxLength={REASON_MAX}
              rows={3} onChange={(event) => setReason(event.target.value)} />
          </label>
          {!reasonValid && reason.length > 0 && <Typography.Text type="danger">
            {t('invoices:workflow.corrections.reasonLength', { min: REASON_MIN, max: REASON_MAX })}
          </Typography.Text>}
          <label>
            <span>{t('invoices:workflow.corrections.withdrawMonth')}</span>
            <DatePicker picker="month" aria-label={t('invoices:workflow.corrections.withdrawMonth')} style={{ width: '100%' }}
              value={month ? dayjs(`${month}-01`) : null} onChange={(date) => setMonth(date?.format('YYYY-MM') ?? '')} />
          </label>
        </Space>
      </Modal>
    </Card>
  );
}
